import { EventEmitter, inject, Injectable, OnDestroy } from '@angular/core';
import { ThemingService } from '@catch/services';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { JobboardAdapter } from '../adapters/jobboard.adapter';
import { CompanyInfo } from '../interfaces/company-info';
import { SEOService } from './seo.service';

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class ColorService implements OnDestroy {
  private readonly jobboardAdapter = inject(JobboardAdapter);
  private readonly seoService = inject(SEOService);
  private readonly themingService = inject(ThemingService);

  private STANDARD_LOGO: string =
    './assets/img/logos/titled/blue_titled_dark.png';
  private STANDARD_LOGO_NEW: string = './assets/img/logos/catch_logo_new.png';
  private STANDARD_ICON: string = './assets/img/favicon.png';
  private STANDARD_ICON_NEW: string = './assets/img/favicon_new.png';
  logoSrc: string = this.STANDARD_LOGO;
  iconSrc: string = this.STANDARD_ICON;
  accent: string = '#fff';
  background: string = '#fff';
  buttonFont: string = '#000';
  companyId: number = null;
  companyInfo: CompanyInfo;

  changeEvent$ = new EventEmitter<void>();

  private setColors(accent: string, background: string, buttonFont: string) {
    this.accent = accent.toString().startsWith('#') ? accent : `#${accent}`;
    this.background = background.toString().startsWith('#')
      ? background
      : `#${background}`;
    this.buttonFont = buttonFont.toString().startsWith('#')
      ? buttonFont
      : `#${buttonFont}`;
    document.documentElement.style.setProperty('--accent-1', this.background);
    document.documentElement.style.setProperty('--accent-2', this.accent);
    document.documentElement.style.setProperty('--font-color', this.buttonFont);
  }

  public setDefaultDesign() {
    this.setColors(
      this.themingService.accent,
      this.themingService.background,
      '#fff'
    );
    this.seoService.setWebsite();
    this.logoSrc = this.STANDARD_LOGO;
    this.iconSrc = this.STANDARD_ICON;
    this.setFavicon(this.iconSrc);
    setTimeout(() => {
      this.companyId = null;
      this.changeEvent$.emit();
    });
  }

  setDesign(companyId?: number) {
    if (companyId === this.companyId) return;

    if (!companyId) {
      this.setDefaultDesign();
      return;
    }

    this.companyId = companyId;
    this.loadColors(companyId);
    this.loadGeneralInfo(companyId);
  }

  private loadColors(id: number) {
    this.jobboardAdapter
      .getDesign(id)
      .pipe(untilDestroyed(this))
      .subscribe((result) => {
        this.setColors(result.accent, result.background, result.buttonFont);
        this.changeEvent$.emit();
      });
  }

  loadGeneralInfo(id: number) {
    this.jobboardAdapter
      .getCompanyInfo(id)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (result: CompanyInfo) => {
          this.companyInfo = result;
          this.logoSrc = result.logoURL ?? this.STANDARD_LOGO;
          this.iconSrc = result.iconURL ?? this.STANDARD_ICON;
          this.setFavicon(this.iconSrc);
          this.changeEvent$.emit();
        },
      });
  }

  private setFavicon(src: string) {
    const favIcon: HTMLLinkElement = document.querySelector('#favicon');
    favIcon.href = src;
  }

  ngOnDestroy(): void {
    // This method is required for untilDestroyed to work properly
    // The actual unsubscription is handled by the untilDestroyed operator
  }
}
